import React, { useCallback, useState, useMemo } from 'react';
import {
  View,
  ScrollView,
  Pressable,
  TouchableOpacity,
  ImageBackground,
  Image,
  ActivityIndicator,
} from 'react-native';
import Text from "@components/CustomText";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import HeaderWithBackAndTitle from '@components/HeaderWithBackAndTitle';
import { useDispatch, useSelector } from 'react-redux';
import BackArrow from "@assets/svg/BackArrow";
import ChevronRight from "@assets/svg/ChevronRight";
import { getWalletSummaryV3 } from "@services/api";
import { actions as walletV3Actions } from '@reducers/walletV3Reducer';
import Rectangle1166 from "@assets/images/Rectangle1166.png";
import Maskgroup from "@assets/images/Maskgroup.png";
import { ScaledSheet } from '@utils/scale';



export default function WalletScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const [walletSummary, setWalletSummary] = useState(null);
  const summary = useSelector((state) => state.walletV3Reducer?.walletSummary);
  const isLoading = useSelector((state) => state.walletV3Reducer?.isLoading);

  const fetchData = useCallback(async () => {
    dispatch(walletV3Actions.setWalletV3Loading(true));
    try {
      const { data } = await getWalletSummaryV3();
      setWalletSummary(data);
      dispatch(walletV3Actions.getWalletV3SummarySuccess(data?.summary));
    } catch (error) {
      console.log(error);
      dispatch(walletV3Actions.getWalletV3SummaryFail(error));
    } finally {
      dispatch(walletV3Actions.setWalletV3Loading(false));
    }
  }, [dispatch]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData]),
  );

  const handleWithdrawPress = useCallback(() => {
    navigation.navigate("WithDrawGullakMoney");
  }, [navigation]);

  if (isLoading) {
    return <View style={styles.safe}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: 32 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={[styles.beLikeRow, styles.headContainer]}>
          <TouchableOpacity
            style={{ paddingVertical: 15, paddingRight: 20 }}
            onPress={() => navigation.goBack()}
          >
            <BackArrow />
          </TouchableOpacity>
          <Text bold fontType="head2">
            {'Wallet'}
          </Text>
        </View>

        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      </ScrollView>
    </View>
  }

  return (
    <View style={styles.safe}>
      <HeaderWithBackAndTitle
        headertitle="Wallet"
        subHeaderTitle=""
        headerStyle={{}}
        navigation={navigation}
        route={route}
        rightIcon={null}
        RightButton={null}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
      />
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: 32 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Balance Card */}
        <View style={styles.balanceCardWrapper}>
          {/* base black blurred rounded image */}
          <ImageBackground
            source={Rectangle1166}
            resizeMode="cover"
            style={styles.balanceCard}
            imageStyle={{ borderRadius: 28 }}
          >
            {/* overlay circles */}
            <Image
              source={Maskgroup}
              resizeMode="cover"
              style={styles.overlayShape}
            />

            <Text style={styles.balanceLabel}>{summary?.title}</Text>

            <View style={styles.balanceRow}>
              <Text style={styles.balanceValue}>{summary?.value_txt}</Text>
            </View>

            <Text style={styles.prevBalance}>
              <Text variant='body_text_1' style={{ color: '#D9F99D' }}>{summary?.prev_balance_txt}</Text>
            </Text>

            <Pressable 
              style={styles.withdrawBtn} 
              onPress={handleWithdrawPress}
            >
              <Text style={styles.withdrawText}>{summary?.cta_txt}</Text>
              <ChevronRight width={20} height={20} pathFill="#222" />
            </Pressable>

          </ImageBackground>
        </View>


        {/* Divider with date range */}
        <View style={styles.dividerRow}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>{walletSummary?.date_line}</Text>
          <View style={styles.dividerLine} />
        </View>

        {/* List rows */}
        <View style={{ gap: 12 }}>
          {walletSummary?.transactions?.map((r) => {
            const { key, nav_to, title, value_txt, value_color, nav_params = {}, sub_value_txt } = r;
            return <Row 
            key={key}
            type={key} 
            title={title} 
            amount={value_txt} 
            sub_value_txt={sub_value_txt}
            navigation={navigation} 
            nav_to={nav_to} 
            value_color={value_color} 
            route_params={nav_params} 
          />
          })}
        </View>

        {/* More Information Section */}
        <View style={styles.moreInfoSection}>
          <View style={styles.moreInfoDivider} />
          <Text variant='subtitle_14' style={styles.moreInfoText}>MORE INFORMATION</Text>
          <View style={styles.moreInfoDivider} />
        </View>

        {/* Bottom tiles */}
        <View style={styles.tilesRow}>
          <Tile 
            title={walletSummary?.payout_info?.value_txt} 
            subtitleTop={walletSummary?.payout_info?.title} 
            subtitleBottom={walletSummary?.payout_info?.subtitle} 
            navigation={navigation} 
            nav_to={walletSummary?.payout_info?.nav_to || 'Earnings'} 
            route_params={{ nav_params: walletSummary?.payout_info?.nav_params }} 
          />
          <Tile 
            title="" 
            subtitleTop={walletSummary?.txn_statement_cta_txt} 
            navigation={navigation} 
            nav_to="TransactionStatements" 
            route_params={{ type: '' }} 
          />
        </View>
      </ScrollView>
    </View>
  );
}

const ROW_RIGHT_ALIGN_STYLE = { alignItems: 'flex-end' };

const Row = React.memo(({ title, navigation, nav_to, type, value_color, amount, route_params, sub_value_txt }) => {
  const handlePress = useCallback(() => {
    navigation.navigate(nav_to, { type: type });
  }, [navigation, nav_to, type]);

  const amountStyle = useMemo(() => [styles.rowAmount, { color: value_color || '#000' }], [value_color]);
  const subValueStyle = useMemo(() => [styles.proceedingText, { color: '#2195F2' }], []);

  return (
    <Pressable onPress={handlePress} style={({ pressed }) => [styles.rowCard, pressed && styles.pressed]}>
      <Text variant="title_18_regular" style={styles.rowTitle}>{title}</Text>
      <View style={styles.rowRight}>
        <View style={ROW_RIGHT_ALIGN_STYLE}>
        <Text variant='title_18_semi' style={amountStyle}>
          {amount}
        </Text>
        {sub_value_txt ? <Text variant="subtitle_14" style={subValueStyle}>
          {sub_value_txt}
        </Text> : null}
        </View>
        <ChevronRight width={16} height={16} pathFill="#9CA3AF" />
      </View>
    </Pressable>
  );
});

const TILE_SPACER_STYLE = { height: 8 };

const Tile = React.memo(({ title, subtitleTop, subtitleBottom, navigation, nav_to, route_params }) => {
  const handlePress = useCallback(() => {
    navigation.navigate(nav_to, route_params);
  }, [navigation, nav_to, route_params]);

  const titleStyle = useMemo(() => [styles.tileTitle, { fontWeight: '800' }], []);

  return (
    <Pressable onPress={handlePress} style={({ pressed }) => [styles.tile, pressed && styles.pressed]}>
      <View style={styles.tileHeaderRow}>
        <Text style={titleStyle} numberOfLines={1}>
          {title || ''}
        </Text>
        <View style={styles.tileChevronBubble}>
          <ChevronRight width={18} height={18} pathFill="#9CA3AF" />
        </View>
      </View>
      <View style={TILE_SPACER_STYLE} />
      <Text variant='title_16'>{subtitleTop}</Text>
      {!!subtitleBottom && <Text variant='body_text_3'>{subtitleBottom}</Text>}
    </Pressable>
  );
});

const RADIUS_LG = 16;
const RADIUS_XL = 28;

const styles = ScaledSheet.create({
  headContainer: {
    backgroundColor: "#FFFFFF",
  },
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  safe: { flex: 1, backgroundColor: '#FFFFFF' },
  container: { flex: 1, paddingHorizontal: 16, backgroundColor: "#fff" },
  balanceCardWrapper: {
    marginTop: 8,
    borderRadius: RADIUS_XL,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
    backgroundColor: 'transparent',
  },
  balanceCard: {
    backgroundColor: '#1A1A1A', // fallback (looks like the gradient in your mock)
    borderRadius: RADIUS_XL,
    padding: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  overlayShape: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 150,
    height: 150,
    zIndex: 1,
  },
  balanceLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    opacity: 0.95,
    zIndex: 2,
  },
  balanceRow: { flexDirection: 'row', alignItems: 'flex-end', marginTop: 6, zIndex: 2 },
  balanceValue: { color: '#FFFFFF', fontSize: 44, fontWeight: '900', letterSpacing: 0.5, zIndex: 2 },
  prevBalance: {
    marginTop: 4,
    color: '#E5FF7A',
    fontSize: 13,
    fontWeight: '600',
    opacity: 0.9,
    zIndex: 2,
  },
  withdrawBtn: {
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 16,
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.55)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 2,
  },
  withdrawText: { fontSize: 18, fontWeight: '800', color: '#222' },
  dividerRow: {
    marginTop: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#E5E7EB' },
  dividerText: { paddingHorizontal: 8, fontSize: 14, color: '#6B7280' },
  rowCard: {
    minHeight: 60,
    backgroundColor: '#F2F4F8',
    borderRadius: RADIUS_LG,
    paddingVertical: 16,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  proceedingText: {
    fontStyle: "italic"
  },
  rowTitle: { color: '#111' },
  rowRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowAmount: { color: '#111', lineHeight: 24 },
  rowAmountNegative: { color: '#FF4D31' },
  moreInfoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    marginBottom: 4,
    gap: 8,
  },
  moreInfoDivider: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E7EB',
  },
  moreInfoText: {
    color: '#6B7280',
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },

  tilesRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  tile: {
    flex: 1,
    backgroundColor: '#F2F4F8',
    borderRadius: RADIUS_LG,
    padding: 16,
  },
  tileHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  tileTitle: { fontSize: 20, fontWeight: '700', color: '#111' },
  tileChevronBubble: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileSubtitleTop: { fontSize: 16, fontWeight: '600', color: '#111' },
  tileSubtitleBottom: { fontSize: 13, color: '#6B7280', marginTop: 2 },

  pressed: { opacity: 0.7 },
});
